import React, { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { Upload, Save, FileCheck, Eye, EyeOff, Star, StarOff, Trash2, Image as ImageIcon, X, Tag, Clock } from "lucide-react";
import { toast } from "sonner";

// Resolve a stored image_url (may be relative "/api/files/x" or absolute) to a fetchable URL.
const resolveImageSrc = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${API}${url.slice(4)}`; // API already ends with /api
  return url;
};

// Convert an ISO 8601 timestamp (or null) into the `YYYY-MM-DDTHH:mm` format
// required by <input type="datetime-local">. Returns "" for missing values.
const toDatetimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Pretty UK-style relative for "in 2 days, 4 hours" — used to render the
// current sale-end countdown inline next to the datepicker.
const formatCountdown = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  if (ms <= 0) return "Expired";
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  if (days >= 1) return `in ${days}d ${hours}h`;
  if (hours >= 1) return `in ${hours}h ${mins}m`;
  return `in ${mins}m`;
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});

  const load = () => api.get("/products?only_visible=false").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const startEdit = (p) => {
    setEditing(p.sku);
    setDraft({
      price: p.price,
      stock_quantity: p.stock_quantity,
      batch_number: p.batch_number || "",
      manufacturing_date: p.manufacturing_date || "",
      expiry_date: p.expiry_date || "",
      coa_tested_date: p.coa_tested_date || "",
      featured: p.featured,
      visible: p.visible,
      same_day_dispatch_eligible: p.same_day_dispatch_eligible,
      uk_stock: p.uk_stock,
      image_url: p.image_url || "",
      on_sale: !!p.on_sale,
      compare_at_price: p.compare_at_price ?? "",
      sale_ends_at_local: toDatetimeLocal(p.sale_ends_at),
      // Admin-only — never rendered on the public site. Used by the
      // daily P&L card on the admin dashboard.
      supplier_cost: p.supplier_cost ?? "",
    });
  };

  const save = async (sku) => {
    try {
      // Normalize the draft into the backend's payload shape:
      //  - empty strings → null (so we don't store "")
      //  - convert local datetime back to UTC ISO 8601
      //  - drop our internal "_local" field
      const payload = { ...draft };
      if (payload.image_url === "") payload.image_url = null;
      if (payload.compare_at_price === "" || payload.compare_at_price == null) {
        payload.compare_at_price = null;
      } else {
        payload.compare_at_price = parseFloat(payload.compare_at_price);
      }
      if (payload.supplier_cost === "" || payload.supplier_cost == null) {
        payload.supplier_cost = null;
      } else {
        payload.supplier_cost = parseFloat(payload.supplier_cost);
      }
      if (payload.sale_ends_at_local) {
        payload.sale_ends_at = new Date(payload.sale_ends_at_local).toISOString();
      } else {
        payload.sale_ends_at = null;
      }
      delete payload.sale_ends_at_local;
      await api.patch(`/admin/products/${sku}`, payload);
      toast.success("Product updated");
      setEditing(null);
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  // Quick "extend sale" helper. Reads the current sale_ends_at off the live
  // product doc (not the draft) and pushes it forward by N days. Updates the
  // backend immediately — no need to enter Edit mode. Used for the +1d/+3d/+7d
  // shortcuts next to each on-sale product.
  const extendSale = async (sku, currentEndsAt, days) => {
    if (!currentEndsAt) {
      toast.error("This product isn't on sale yet — set the dates in Edit first");
      return;
    }
    const base = new Date(currentEndsAt);
    if (Number.isNaN(base.getTime())) {
      toast.error("Couldn't read existing sale end date");
      return;
    }
    // If the sale already expired, anchor the extension to now rather than the
    // stale date — otherwise +1d from yesterday is still yesterday.
    const anchor = base.getTime() < Date.now() ? new Date() : base;
    const next = new Date(anchor.getTime() + days * 86400000);
    try {
      await api.patch(`/admin/products/${sku}`, {
        on_sale: true,
        sale_ends_at: next.toISOString(),
      });
      toast.success(`Sale extended +${days}d → ends ${next.toLocaleString("en-GB", { timeZone: "Europe/London", dateStyle: "medium", timeStyle: "short" })}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't extend sale");
    }
  };

  const toggle = async (sku, key, currentVal) => {
    try {
      await api.patch(`/admin/products/${sku}`, { [key]: !currentVal });
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const uploadCOA = async (sku, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/products/${sku}/coa`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("COA uploaded");
      load();
    } catch {
      toast.error("Upload failed");
    }
  };

  const uploadImage = async (sku, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large — please use a file under 8 MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/products/${sku}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Product image updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Image upload failed");
    }
  };

  const clearImage = async (sku) => {
    if (!window.confirm("Clear this product's image? It will fall back to the brand SVG vial.")) return;
    try {
      await api.delete(`/admin/products/${sku}/image`);
      toast.success("Image cleared");
      load();
    } catch {
      toast.error("Failed to clear image");
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="pg-micro mb-2">// Inventory</div>
      <h1 className="text-[#F7FAFC] text-3xl pg-display mb-8" style={{ fontFamily: "'Exo 2'" }}>
        Products & Lab Reports
      </h1>

      <div className="space-y-3">
        {products.map((p) => (
          <div key={p.sku} className="pg-card p-5" data-testid={`admin-product-${p.sku}`}>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
              <div className="lg:col-span-4 flex gap-3">
                <div
                  className="shrink-0 w-16 h-16 rounded-sm border border-[#1E2E44] overflow-hidden bg-[#0B1220] flex items-center justify-center"
                  data-testid={`thumb-${p.sku}`}
                >
                  {p.image_url ? (
                    <img
                      src={resolveImageSrc(p.image_url)}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ImageIcon size={20} className="text-[#9CA8B8]/40" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="pg-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA8B8] mb-1">{p.sku}</div>
                  <div className="text-[#F7FAFC] pg-heading text-lg leading-tight" style={{ fontFamily: "'Rajdhani'" }}>
                    {p.name} {p.strength}
                  </div>
                  <div className="text-xs text-[#9CA8B8]">{p.category}</div>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {p.stock_status === "in_stock" && <span className="pg-badge-stock">In Stock</span>}
                    {p.stock_status === "out_of_stock" && <span className="pg-badge-out">Out of Stock</span>}
                    {p.stock_status === "low_stock" && <span className="pg-badge-cyan">Low Stock</span>}
                    {p.featured && <span className="pg-badge-cyan">Featured</span>}
                    {!p.visible && <span className="pg-badge-out">Hidden</span>}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5 grid grid-cols-2 gap-2">
                {editing === p.sku ? (
                  <>
                    <Field label="Price (£)">
                      <input type="number" step="0.01" value={draft.price} onChange={(e) => setDraft({ ...draft, price: parseFloat(e.target.value) })} className="pg-input text-sm" data-testid={`edit-price-${p.sku}`} />
                    </Field>
                    <Field label="Supplier cost £ / vial (internal)">
                      <input
                        type="number"
                        step="0.01"
                        placeholder="e.g. 12.50"
                        value={draft.supplier_cost}
                        onChange={(e) => setDraft({ ...draft, supplier_cost: e.target.value })}
                        className="pg-input text-sm"
                        data-testid={`edit-supplier-cost-${p.sku}`}
                      />
                    </Field>
                    <Field label="Stock Qty">
                      <input type="number" value={draft.stock_quantity} onChange={(e) => setDraft({ ...draft, stock_quantity: parseInt(e.target.value || "0") })} className="pg-input text-sm" data-testid={`edit-qty-${p.sku}`} />
                    </Field>
                    <Field label="Batch">
                      <input value={draft.batch_number} onChange={(e) => setDraft({ ...draft, batch_number: e.target.value })} className="pg-input text-sm" data-testid={`edit-batch-${p.sku}`} />
                    </Field>
                    <Field label="MFG Date">
                      <input type="date" value={draft.manufacturing_date} onChange={(e) => setDraft({ ...draft, manufacturing_date: e.target.value })} className="pg-input text-sm" />
                    </Field>
                    <Field label="Expiry">
                      <input type="date" value={draft.expiry_date} onChange={(e) => setDraft({ ...draft, expiry_date: e.target.value })} className="pg-input text-sm" />
                    </Field>
                    <Field label="COA Tested Date">
                      <input
                        type="text"
                        placeholder="e.g. 17 Jan 2026 (must match COA PDF)"
                        value={draft.coa_tested_date}
                        onChange={(e) => setDraft({ ...draft, coa_tested_date: e.target.value })}
                        className="pg-input text-sm"
                        data-testid={`edit-coa-tested-date-${p.sku}`}
                      />
                    </Field>
                    <div className="col-span-2">
                      <Field label="Image URL (or use Upload below)">
                        <input
                          type="text"
                          value={draft.image_url}
                          onChange={(e) => setDraft({ ...draft, image_url: e.target.value })}
                          placeholder="https://… or leave blank for SVG"
                          className="pg-input text-sm"
                          data-testid={`edit-image-url-${p.sku}`}
                        />
                      </Field>
                    </div>

                    {/* Sale controls — full row */}
                    <div className="col-span-2 mt-2 p-3 rounded-sm border border-[#A6FF00]/30 bg-[#A6FF00]/[0.04]">
                      <div className="flex items-center justify-between mb-2">
                        <div className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#A6FF00] flex items-center gap-1.5">
                          <Tag size={11} /> Flash Sale
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!draft.on_sale}
                            onChange={(e) => setDraft({ ...draft, on_sale: e.target.checked })}
                            className="accent-[#A6FF00]"
                            data-testid={`edit-on-sale-${p.sku}`}
                          />
                          <span className="text-[11px] text-[#F7FAFC]">On sale</span>
                        </label>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Original price (£)">
                          <input
                            type="number"
                            step="0.01"
                            value={draft.compare_at_price}
                            onChange={(e) => setDraft({ ...draft, compare_at_price: e.target.value })}
                            placeholder="e.g. 79.00"
                            className="pg-input text-sm"
                            data-testid={`edit-compare-${p.sku}`}
                          />
                        </Field>
                        <Field label="Sale ends at (UK time)">
                          <input
                            type="datetime-local"
                            value={draft.sale_ends_at_local}
                            onChange={(e) => setDraft({ ...draft, sale_ends_at_local: e.target.value })}
                            className="pg-input text-sm"
                            data-testid={`edit-sale-end-${p.sku}`}
                          />
                        </Field>
                      </div>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {[1, 3, 7].map((d) => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => {
                              const base = draft.sale_ends_at_local
                                ? new Date(draft.sale_ends_at_local)
                                : new Date();
                              const anchor = base.getTime() < Date.now() ? new Date() : base;
                              const next = new Date(anchor.getTime() + d * 86400000);
                              setDraft({
                                ...draft,
                                on_sale: true,
                                sale_ends_at_local: toDatetimeLocal(next.toISOString()),
                              });
                            }}
                            className="text-[10px] font-bold uppercase tracking-[0.18em] py-1 px-2.5 rounded-sm border border-[#A6FF00]/40 text-[#A6FF00] hover:bg-[#A6FF00]/10"
                            data-testid={`extend-sale-draft-${d}d-${p.sku}`}
                          >
                            +{d}d
                          </button>
                        ))}
                        {draft.sale_ends_at_local && (
                          <button
                            type="button"
                            onClick={() => setDraft({ ...draft, sale_ends_at_local: "", on_sale: false })}
                            className="text-[10px] font-bold uppercase tracking-[0.18em] py-1 px-2.5 rounded-sm border border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10"
                            data-testid={`clear-sale-${p.sku}`}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <p className="text-[10px] text-[#9CA8B8] mt-2 leading-relaxed">
                        When the timer expires, the price auto-reverts to the original price and the sale flag clears. Hit "Save" to apply.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <Stat label="Price" value={`£${p.price.toFixed(2)}`} />
                    <Stat label="Stock" value={p.stock_quantity} />
                    <Stat label="Batch" value={p.batch_number || "—"} />
                    <Stat label="Expiry" value={p.expiry_date || "—"} />
                    {p.on_sale && p.sale_ends_at && (
                      <div className="col-span-2 p-2.5 rounded-sm border border-[#A6FF00]/30 bg-[#A6FF00]/[0.05]">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="text-[11px] text-[#F7FAFC] flex items-center gap-1.5 min-w-0">
                            <Clock size={11} className="text-[#A6FF00] shrink-0" />
                            <span className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#A6FF00]">Sale ends</span>
                            <span className="text-[#9CA8B8] truncate">
                              {new Date(p.sale_ends_at).toLocaleString("en-GB", { timeZone: "Europe/London", dateStyle: "medium", timeStyle: "short" })}
                              <span className="text-[#5EEAD4] ml-1">· {formatCountdown(p.sale_ends_at)}</span>
                            </span>
                          </div>
                          <div className="flex gap-1.5 flex-wrap">
                            {[1, 3, 7].map((d) => (
                              <button
                                key={d}
                                type="button"
                                onClick={() => extendSale(p.sku, p.sale_ends_at, d)}
                                className="text-[9px] font-bold uppercase tracking-[0.18em] py-1 px-2 rounded-sm border border-[#A6FF00]/40 text-[#A6FF00] hover:bg-[#A6FF00]/15"
                                data-testid={`quick-extend-${d}d-${p.sku}`}
                              >
                                +{d}d
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="lg:col-span-3 flex flex-col gap-2">
                <div className="flex gap-2 flex-wrap">
                  <IconBtn onClick={() => toggle(p.sku, "visible", p.visible)} active={p.visible} title="Visible" data-testid={`toggle-visible-${p.sku}`}>
                    {p.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                  </IconBtn>
                  <IconBtn onClick={() => toggle(p.sku, "featured", p.featured)} active={p.featured} title="Featured" data-testid={`toggle-featured-${p.sku}`}>
                    {p.featured ? <Star size={14} /> : <StarOff size={14} />}
                  </IconBtn>
                </div>
                {editing === p.sku ? (
                  <>
                    <button onClick={() => save(p.sku)} className="pg-btn-primary text-xs" data-testid={`save-${p.sku}`}>
                      <Save size={14} /> Save
                    </button>
                    <button onClick={() => setEditing(null)} className="pg-btn-ghost text-xs">Cancel</button>
                  </>
                ) : (
                  <button onClick={() => startEdit(p)} className="pg-btn-ghost text-xs" data-testid={`edit-${p.sku}`}>Edit</button>
                )}

                <label className="pg-btn-ghost text-xs cursor-pointer" data-testid={`upload-image-label-${p.sku}`}>
                  <ImageIcon size={14} /> {p.image_url ? "Replace image" : "Upload image"}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => {
                      uploadImage(p.sku, e.target.files[0]);
                      e.target.value = ""; // allow re-upload of same file
                    }}
                    data-testid={`upload-image-${p.sku}`}
                  />
                </label>

                {p.image_url && (
                  <button
                    type="button"
                    onClick={() => clearImage(p.sku)}
                    className="pg-btn-ghost text-xs"
                    data-testid={`clear-image-${p.sku}`}
                  >
                    <X size={14} /> Clear image
                  </button>
                )}

                <label className="pg-btn-ghost text-xs cursor-pointer">
                  <Upload size={14} /> {p.coa_filename ? "Replace COA" : "Upload COA"}
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    className="hidden"
                    onChange={(e) => uploadCOA(p.sku, e.target.files[0])}
                    data-testid={`upload-coa-${p.sku}`}
                  />
                </label>

                {p.coa_filename && (
                  <a
                    href={`${API}/files/${p.coa_filename}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] pg-mono uppercase tracking-[0.2em] text-[#A6FF00] hover:underline flex items-center gap-1"
                    data-testid={`view-coa-${p.sku}`}
                  >
                    <FileCheck size={12} /> View Lab Report
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="pg-micro">{label}</div>
      <div className="text-[#F7FAFC] text-sm pg-mono">{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="pg-micro mb-1">{label}</div>
      {children}
    </label>
  );
}

function IconBtn({ children, active, ...rest }) {
  return (
    <button
      {...rest}
      className={`w-8 h-8 border rounded-sm flex items-center justify-center transition-colors ${
        active ? "border-[#A6FF00] text-[#A6FF00] bg-[#A6FF00]/10" : "border-[#9CA8B8]/30 text-[#9CA8B8] hover:border-[#00C2D6]"
      }`}
    >
      {children}
    </button>
  );
}
